import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import {Box, Button, Loading, Typography} from '@viptech/react-components'
import {useSnackbar} from 'notistack'
import {useCallback, useEffect, useState} from 'react'
import {DrawBarrierWithSvg} from 'src/common/components/draw-lines-only/DrawBarrierWithSvg'
import useSnapshot from 'src/hooks/api/camera/useSnapshot'
import useDeleteArea from 'src/hooks/api/interest-areas/useDeleteArea'
import useInterestArea from 'src/hooks/api/interest-areas/useInterestAreas'
import usePersonalizedThemeByCompany from 'src/hooks/usePersonalizedThemeByCompany'
import NoImage from 'src/images/noImage.svg'
import {CameraModalRegisterBarrier} from 'src/pages/camera-config/components/camera-barriers/components/CameraModalRegisterBarrier'
import {ListBarriersOnCards} from 'src/pages/camera-config/components/camera-barriers/components/ListBarriersOnCards'
import {useCameraConfigContext} from 'src/pages/camera-config/context/CameraConfigContext'
import {BarrierTypeResponse} from 'src/services/api'
import handleErrorWithSnackbar from 'src/utilities/handleErrorWithSnackbar'
import styled from 'styled-components'
import {DetectionsContainer} from './BarriersBase'

export function Barriers() {
  const {enqueueSnackbar} = useSnackbar()
  const {camera} = useCameraConfigContext()
  const {styledTheme} = usePersonalizedThemeByCompany()

  const {
    data: barriers,
    refetch: refetchBarriers,
    isLoading: loadingBarriers,
  } = useInterestArea({
    cameraId: camera?.id ?? -1,
    onError: (error) => handleErrorWithSnackbar(enqueueSnackbar, error, 'Erro ao carregar áreas'),
    enabled: !!camera?.id,
  })

  const {data: snapshot, isLoading: loadingSnapshot} = useSnapshot({
    cameraId: camera?.id,
    enabled: !!camera?.id,
  })

  const [interestAreasToList, setInterestAreasToList] = useState<BarrierTypeResponse[]>([])

  const [selectedBarrier, setSelectedBarrier] = useState<BarrierTypeResponse | null>(null)
  const [openCreateAreaModal, setOpenCreateAreaModal] = useState(false)

  const formatInterestAreaData = () => {
    if (!barriers) return
    setInterestAreasToList(
      barriers.map((it) => {
        const points = it.coordinates.map((point) => {
          return {
            ...point,
            x: point.x,
            y: point.y,
          }
        })
        return {
          ...it,
          coordinates: points,
        }
      }),
    )
  }

  useEffect(() => {
    formatInterestAreaData()
  }, [barriers])

  useEffect(() => {
    if (!selectedBarrier?.id || !barriers) return
    const updated = barriers.find((barrier) => barrier.id === selectedBarrier.id)
    if (updated) {
      setSelectedBarrier(updated)
    }
  }, [barriers, selectedBarrier?.id])

  const handleBarrierSaved = useCallback(
    async (barrier: BarrierTypeResponse) => {
      const {data} = await refetchBarriers()
      const updated = data?.find((item) => item.id === barrier.id) ?? barrier
      setSelectedBarrier(updated)
    },
    [refetchBarriers],
  )

  const {mutateAsync: deleteSelectedBarrier} = useDeleteArea()

  const handleDeleteBarrier = async () => {
    if (!selectedBarrier?.id) return
    try {
      await deleteSelectedBarrier(selectedBarrier.id)
      setSelectedBarrier(null)
    } catch {}
  }

  return (
    <Box display='flex' justifyContent='center' columnGap='30px' minHeight='500px' width='100%' height='100%'>
      <DetectionsContainer>
        {loadingSnapshot ? (
          <Box height='320px' width='100%' display='flex' justifyContent='center' align='center'>
            <Loading size='30px' />
          </Box>
        ) : (
          <Box display='flex' align='center' justifyContent='center' direction='column' width='100%' padding='10px'>
            {snapshot && (
              <DrawBarrierWithSvg
                key={selectedBarrier?.id ?? 'no-barrier'}
                imageSrc={snapshot.time.base64}
                points={selectedBarrier?.coordinates}
                width={700}
                height={400}
              />
            )}
            {!snapshot && <img src={NoImage} alt='sem imagem' width={'250px'} style={{marginBottom: '50px'}} />}
          </Box>
        )}
      </DetectionsContainer>

      <ListContainer>
        <Typography variant='p' weight={700} children='Barreiras Cadastradas' />

        <Box display='flex' direction='column' height='100%' paddingTop='15px'>
          {loadingBarriers ? (
            <Box position='absolute' right='20px'>
              <Loading size='20px' />
            </Box>
          ) : (
            <ListBarriersOnCards
              barriers={interestAreasToList}
              selected={selectedBarrier}
              onClickEdit={() => {
                if (!snapshot?.time.base64) return enqueueSnackbar('Não é possível editar a barreira no momento. Imagem indisponível.', {variant: 'info'})
                setOpenCreateAreaModal(true)
              }}
              onClickDelete={handleDeleteBarrier}
              onSelectChange={(it) => setSelectedBarrier(it)}
            />
          )}
        </Box>

        <Box display='flex' justifyContent='center'>
          <Button
            variant='outlined'
            disabled={!snapshot}
            onClick={() => {
              setSelectedBarrier(null)
              setOpenCreateAreaModal(true)
            }}
            leftIcon={<AddCircleOutlineIcon sx={{fontSize: '15px', textTransform: 'initial', color: styledTheme.colors.primary}} />}
            fontSize='13px'>
            Criar nova barreira
          </Button>
        </Box>
      </ListContainer>

      {openCreateAreaModal && snapshot && snapshot.time.base64 && (
        <CameraModalRegisterBarrier
          open={openCreateAreaModal}
          setOpen={setOpenCreateAreaModal}
          editingArea={barriers?.find((it) => it.id === selectedBarrier?.id)}
          setEditingArea={setSelectedBarrier}
          snapshot={snapshot.time.base64}
          refetchBarriers={refetchBarriers}
          onBarrierSaved={handleBarrierSaved}
        />
      )}
    </Box>
  )
}

const ListContainer = styled.div`
  display: flex;
  justify-content: space-between;
  flex-direction: column;
  min-width: 390px;
  padding: 20px;
  margin-top: 10px;
  border-radius: 8px;
  position: relative;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);

  @media (max-height: 600px) {
    min-width: 270px;
  }
`
